import { FC, memo } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Member/CurrentPlan/PaymentAlert/index";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 支付警告类型枚举
 */
export enum AlertType {
  PAYMENT_FAILED = "paymentFailed",
  PAYMENT_PENDING = "paymentPending",
  SUBSCRIPTION_EXPIRING = "subscriptionExpiring",
}

interface PaymentAlertProps {
  alertType?: AlertType;
  onViewDetails?: () => void;
  toMemberPrice?: () => void;
  closePaymentError?: () => void;
  remainDay?: number;
  name?: string;
  isShowViewDetails?: boolean;
}

/**
 * 支付提醒组件，显示支付失败等警告信息
 */
/**
 * 渲染支付失败警告
 */
const renderPaymentFailedAlert = (
  onViewDetails?: () => void,
  onClose?: () => void,
  isShowViewDetails?: boolean,
) => (
  <div
    data-has-button="True"
    data-has-description="True"
    data-type="Error"
    className="self-stretch p-3 bg-background_accent2_overlay5 rounded-md flex flex-col justify-start items-start gap-1 overflow-hidden w-full"
  >
    <div className="self-stretch inline-flex justify-between items-center w-full">
      <div className="inline-flex flex-col justify-center items-start gap-2 w-full">
        <div className="flex flex-row justify-between items-center gap-2 w-full">
          <div className="inline-flex justify-start items-center gap-1">
            <div className="size-4 flex justify-start items-start">
              <Icon
                name="Alerts"
                size={16}
                className="text-content_accent2_primary"
              />
            </div>
            <div className="justify-center text-content_primary title_m">
              {I18n.t("Payment Failed")}
            </div>
          </div>
          <div
            data-openreplay-label="PaymentAlert · div"
            onClick={onClose}
            className="cursor-pointer"
          >
            <Icon
              name="close"
              size={24}
              className="text-black md:text-white/30"
            />
          </div>
        </div>
        <div className="justify-start text-white/40 body_s">
          {I18n.t(
            "We couldn't process your automatic renewal payment. Verify your payment method to prevent service interruptions.",
          )}
          {/** show view details button if isShowViewDetails is true */}
          {isShowViewDetails && (
            <div
              data-openreplay-label="PaymentAlert · div"
              onClick={onViewDetails}
              className="cursor-pointer inline-flex ml-1"
            >
              <div className="justify-start body_medium_s text-content_accent2_primary underline underline-[#ff7375] underline-offset-2">
                {I18n.t("View Details")}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

/**
 * 渲染订阅即将过期警告
 */
const renderSubscriptionExpiringAlert = (
  toMemberPrice?: () => void,
  remainDay?: number,
  name?: string,
) => {
  const remainDayText =
    (remainDay || 0) <= 0
      ? `${name} ${I18n.t("expires today")}`
      : I18n.t("Your Plan ends in %{var0} days.", {
          var0: remainDay,
        });
  return (
    <div
      data-has-button="True"
      data-has-description="False"
      data-type="Warning"
      className="w-full max-w-[740px] rounded-md bg-background_white_overlay5 p-2 flex items-center justify-between gap-2"
    >
      <div className="flex items-center gap-1 min-w-0">
        <Icon
          name="calendar-star"
          size={16}
          className="shrink-0 text-content_quaternary"
        />
        <p className="body_bold_m text-content_primary truncate">
          {remainDayText}
        </p>
      </div>
      <button
        type="button"
        className="button xs_button primary_button button_s shrink-0 min-w-[80px]"
        onClick={toMemberPrice}
      >
        {I18n.t("Upgrade")}
      </button>
    </div>
  );
};

/**
 * 支付提醒组件，显示支付失败等警告信息
 */
const PaymentAlert: FC<PaymentAlertProps> = ({
  alertType = AlertType.PAYMENT_FAILED,
  onViewDetails,
  toMemberPrice,
  remainDay,
  closePaymentError,
  name,
  isShowViewDetails,
}) => {
  // 增加
  // 订阅即将过期渲染
  if (alertType === AlertType.SUBSCRIPTION_EXPIRING) {
    return renderSubscriptionExpiringAlert(toMemberPrice, remainDay, name);
  }

  // 支付失败渲染
  if (alertType === AlertType.PAYMENT_FAILED) {
    return renderPaymentFailedAlert(
      onViewDetails,
      closePaymentError,
      isShowViewDetails,
    );
  }

  // 支付待处理渲染(未实现)
  if (alertType === AlertType.PAYMENT_PENDING) {
    // 未来可扩展
    return null;
  }

  // 默认渲染空内容，当 alertType 不是支持的类型时
  return null;
};

export default memo(PaymentAlert);
